<template>
  <section class="space-y-6">
    <div class="overflow-hidden rounded-[32px] border border-[#eadfd4] bg-[linear-gradient(135deg,#fff8f1_0%,#fff4ea_52%,#fffdf8_100%)] shadow-sm">
      <div class="grid gap-6 px-8 py-8 xl:grid-cols-[minmax(0,1.35fr)_360px] xl:px-10">
        <div class="space-y-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div class="flex items-start gap-4">
              <button type="button" class="mt-1 text-xl text-muted transition hover:text-brand" @click="goBack">
                <i class="ri-arrow-left-line"></i>
              </button>
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.22em] text-[#b78969]">Venue Detail</p>
                <h1 class="mt-3 text-[2.4rem] font-semibold leading-tight text-ink">{{ venue.title || 'Venue detail' }}</h1>
                <div class="mt-3 flex flex-wrap items-center gap-2 text-sm text-[#6e5a49]">
                  <span class="rounded-full bg-white px-3 py-1 font-semibold text-brand shadow-sm ring-1 ring-[#eadfd4]">
                    {{ venue.type || 'Venue' }}
                  </span>
                  <span class="rounded-full bg-white px-3 py-1 font-semibold text-[#7b624d] shadow-sm ring-1 ring-[#eadfd4]">
                    {{ venue.status || 'Draft' }}
                  </span>
                  <span class="inline-flex items-center gap-2 rounded-full bg-white/85 px-3 py-1 ring-1 ring-[#eadfd4]">
                    <i class="ri-building-line text-brand"></i>
                    {{ businessName }}
                  </span>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-3">
              <button
                type="button"
                class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
                @click="goBack"
              >
                Back to business
              </button>
              <button
                type="button"
                class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
                @click="openVenueEditor"
              >
                Edit venue
              </button>
            </div>
          </div>

          <p class="max-w-3xl text-sm leading-7 text-[#5f4b3c]">
            {{ venue.about || venue.tagLine || 'This is the dedicated vendor-side venue detail workspace. We can shape the final customer or ops UI structure once you share the exact layout you want next.' }}
          </p>

          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <article
              v-for="item in statCards"
              :key="item.label"
              class="rounded-[24px] border border-[#eadfd4] bg-white/88 p-5 shadow-[0_12px_30px_rgba(113,78,46,0.06)] backdrop-blur"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff2e5] text-brand">
                  <i :class="item.icon"></i>
                </span>
                <span class="text-[1.8rem] font-semibold leading-none text-ink">{{ item.value }}</span>
              </div>
              <p class="mt-4 text-sm font-semibold text-ink">{{ item.label }}</p>
              <p class="mt-1 text-xs leading-5 text-muted">{{ item.meta }}</p>
            </article>
          </div>
        </div>

        <aside class="space-y-4 rounded-[28px] border border-[#eadfd4] bg-white/92 p-6 shadow-[0_12px_30px_rgba(113,78,46,0.06)]">
          <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Current Scope</p>
            <div class="mt-4 space-y-3">
              <div
                v-for="item in readinessItems"
                :key="item.label"
                class="flex items-start gap-3 rounded-2xl border px-4 py-3"
                :class="item.done ? 'border-emerald-200 bg-emerald-50' : 'border-[#eadfd4] bg-white'"
              >
                <span
                  class="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm"
                  :class="item.done ? 'bg-emerald-700 text-white' : 'bg-[#d8ccc2] text-white'"
                >
                  <i :class="item.done ? 'ri-check-line' : 'ri-error-warning-line'"></i>
                </span>
                <div>
                  <p class="text-sm font-semibold text-ink">{{ item.label }}</p>
                  <p class="mt-1 text-xs leading-5 text-muted">{{ item.meta }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-[22px] border border-[#eadfd4] bg-white p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Address</p>
            <p class="mt-3 text-sm leading-7 text-[#4b3b2f]">{{ venue.address || 'Address not available' }}</p>
          </div>
        </aside>
      </div>
    </div>

    <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
      Loading venue details...
    </div>

    <div v-else-if="error" class="rounded-[28px] border border-red-100 bg-red-50 p-6 text-sm font-semibold text-red-700">
      {{ error }}
    </div>

    <template v-else>
      <div class="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_360px]">
        <div class="space-y-6">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue Overview</p>
                <h2 class="mt-2 font-serif text-[2rem] font-semibold text-ink">Current venue record</h2>
              </div>
              <button type="button" class="rounded-full border border-[#e5d8cc] px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand" @click="openVenueEditor">
                Open editor
              </button>
            </div>

            <div class="mt-6 grid gap-4 md:grid-cols-2">
              <article v-for="item in overviewCards" :key="item.label" class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">{{ item.label }}</p>
                <p class="mt-2 text-base font-semibold text-ink">{{ item.value }}</p>
                <p v-if="item.meta" class="mt-1 text-xs text-muted">{{ item.meta }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Snapshot</p>
            <h2 class="mt-2 font-serif text-[2rem] font-semibold text-ink">Supporting modules</h2>

            <div class="mt-5 grid gap-4 md:grid-cols-3">
              <article class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Spaces</p>
                <p class="mt-2 text-[1.8rem] font-semibold text-ink">{{ spaces.length }}</p>
                <p class="mt-1 text-sm text-muted">Bookable areas configured for this venue.</p>
              </article>
              <article class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Packages</p>
                <p class="mt-2 text-[1.8rem] font-semibold text-ink">{{ packages.length }}</p>
                <p class="mt-1 text-sm text-muted">Venue-level package offerings currently attached.</p>
              </article>
              <article class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Partners</p>
                <p class="mt-2 text-[1.8rem] font-semibold text-ink">{{ partners.length }}</p>
                <p class="mt-1 text-sm text-muted">Preferred partner contacts linked to this venue.</p>
              </article>
            </div>
          </section>
        </div>

        <aside class="space-y-6">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <h3 class="font-serif text-[1.6rem] font-semibold text-ink">Quick actions</h3>
            <div class="mt-5 space-y-3">
              <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="openVenueEditor">
                <span class="text-sm font-semibold text-ink">Edit venue details</span>
                <i class="ri-arrow-right-line text-brand"></i>
              </button>
              <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="openVenueEditorSection('photos')">
                <span class="text-sm font-semibold text-ink">Manage photo library</span>
                <i class="ri-arrow-right-line text-brand"></i>
              </button>
              <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="openVenueEditorSection('spaces')">
                <span class="text-sm font-semibold text-ink">Manage spaces</span>
                <i class="ri-arrow-right-line text-brand"></i>
              </button>
            </div>
          </section>
        </aside>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const businessName = ref('Business')
const venue = ref({
  id: '',
  title: '',
  type: '',
  tagLine: '',
  about: '',
  address: '',
  status: ''
})
const spaces = ref([])
const packages = ref([])
const partners = ref([])

const normalizeVenue = (item = {}) => ({
  id: String(item?.id || item?.venue_id || item?.vendor_business_venue_id || route.params.venueId || ''),
  title: item?.title || item?.name || 'Untitled venue',
  type: item?.type || item?.venue_type || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  about: item?.about || '',
  address: item?.address || item?.location || '',
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft'
})

const statCards = computed(() => [
  { label: 'Spaces', value: spaces.value.length, meta: 'Areas configured for booking at this venue.', icon: 'ri-layout-grid-line' },
  { label: 'Packages', value: packages.value.length, meta: 'Current package offers attached to the venue.', icon: 'ri-price-tag-3-line' },
  { label: 'Partners', value: partners.value.length, meta: 'Preferred vendors linked for ops or recommendations.', icon: 'ri-hand-heart-line' },
  { label: 'Status', value: venue.value.status || 'Draft', meta: 'Current venue record state.', icon: 'ri-checkbox-circle-line' }
])

const readinessItems = computed(() => [
  { label: 'Venue basics available', done: Boolean(venue.value.title && venue.value.address), meta: 'Title and address are present.' },
  { label: 'Spaces attached', done: spaces.value.length > 0, meta: 'At least one venue space is configured.' },
  { label: 'Packages attached', done: packages.value.length > 0, meta: 'Venue has at least one package configured.' },
  { label: 'Partner references added', done: partners.value.length > 0, meta: 'Preferred vendor partner entries are available.' }
])

const overviewCards = computed(() => [
  { label: 'Venue name', value: venue.value.title || 'Not available' },
  { label: 'Venue type', value: venue.value.type || 'Not available' },
  { label: 'Tagline', value: venue.value.tagLine || 'Not available' },
  { label: 'Address', value: venue.value.address || 'Not available' }
])

const loadVenue = async () => {
  loading.value = true
  error.value = ''

  try {
    const businessFormData = new FormData()
    businessFormData.append('business_id', route.params.id)
    const businessResponse = await api.editBusinessVendor(businessFormData)
    const businessPayload = Array.isArray(businessResponse?.data?.data)
      ? businessResponse.data.data[0]
      : businessResponse?.data?.data || businessResponse?.data || {}
    businessName.value = businessPayload?.business_name || businessPayload?.businessName || businessPayload?.name || 'Business'

    const venueFormData = new FormData()
    venueFormData.append('business_id', route.params.id)
    venueFormData.append('venue_id', route.params.venueId)
    const venueResponse = await api.listVenuesBusinessVendor(venueFormData)
    const venuePayload = venueResponse?.data?.data || venueResponse?.data || {}
    const venueItems = Array.isArray(venuePayload)
      ? venuePayload
      : venuePayload?.venues || venuePayload?.items || venuePayload?.list || venuePayload?.venue_list || []
    const matchedVenue = Array.isArray(venueItems)
      ? venueItems.find((item) => String(item?.id || item?.venue_id || item?.vendor_business_venue_id || '') === String(route.params.venueId))
      : null
    venue.value = normalizeVenue(matchedVenue || venueItems[0] || {})

    const spacesFormData = new FormData()
    spacesFormData.append('venue_id', route.params.venueId)
    const packagesFormData = new FormData()
    packagesFormData.append('venue_id', route.params.venueId)
    const partnersFormData = new FormData()
    partnersFormData.append('venue_id', route.params.venueId)

    const [spacesResponse, packagesResponse, partnersResponse] = await Promise.all([
      api.listVenueSpaces(spacesFormData),
      api.getPackagesVenue(packagesFormData),
      api.listVenueVendorPartners(partnersFormData)
    ])

    const spaceData = spacesResponse?.data?.data || spacesResponse?.data || {}
    const packageData = packagesResponse?.data?.data || packagesResponse?.data || {}
    const partnerData = partnersResponse?.data?.data || partnersResponse?.data || {}

    spaces.value = Array.isArray(spaceData) ? spaceData : spaceData?.spaces || spaceData?.items || spaceData?.list || []
    packages.value = Array.isArray(packageData) ? packageData : packageData?.packages || packageData?.items || packageData?.list || []
    partners.value = Array.isArray(partnerData) ? partnerData : partnerData?.partners || partnerData?.vendor_partners || partnerData?.items || partnerData?.list || []
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to load venue details'
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.push({
    name: 'business-detail',
    params: { id: route.params.id }
  })
}

const openVenueEditorSection = (section = 'overview') => {
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: route.params.id,
      moduleType: 'venue',
      moduleId: route.params.venueId
    },
    query: {
      businessName: businessName.value,
      section
    }
  })
}

const openVenueEditor = () => {
  openVenueEditorSection('overview')
}

onMounted(() => {
  loadVenue()
})
</script>
